import type { EditableFloorPlan } from '../models/editor-feature';
import type { FloorPlanRoomAssignment } from '../models/floorplan-room';

/**
 * Puts each desk in the room the database says it is in.
 *
 * The floorplan's own GeoJSON is not the authority here and never has been: booking reads
 * the Workplaces table, and the stored `roomId` is an echo written on the last save — absent
 * entirely from every plan imported before the editor existed. Loading the plan and then
 * applying this is what makes the editor show the same room the app books into.
 *
 * A desk with no matching workplace is left with a null room, which is the honest answer: it
 * is drawn, and it is not bookable.
 */
export const applyRoomAssignments = (
  floorPlan: EditableFloorPlan,
  assignments: readonly FloorPlanRoomAssignment[],
  options: { readonly keepDrawnRooms?: boolean } = {},
): EditableFloorPlan => {
  const byWorkplaceId = new Map(assignments.map((assignment) => [assignment.workplaceId, assignment.roomId]));

  return {
    ...floorPlan,
    desks: floorPlan.desks.map((desk) => {
      // In a draft, a desk placed in a room has no workplace yet — it gets one when the draft is
      // published — so the room it was given is the draft's own answer, and is kept.
      const drawn = options.keepDrawnRooms ? desk.roomId : null;
      const roomId = byWorkplaceId.get(desk.featureId) ?? drawn;

      // Returned unchanged when nothing moved, so the document's identity comparison keeps
      // reporting a freshly loaded plan as clean rather than as an unsaved edit.
      return desk.roomId === roomId ? desk : { ...desk, roomId };
    }),
  };
};

/**
 * The desks that are actually bookable: the ones with a workplace behind them.
 *
 * Not the same as "came back from the server". 35 workplace features already stored have no
 * Workplaces row, and 113 more across eight plans name a workplace belonging to a different
 * office — those desks render for everyone and can be booked by no one.
 */
export const bookableFeatureIdsOf = (assignments: readonly FloorPlanRoomAssignment[]): Set<string> => new Set(
  assignments.map((assignment) => assignment.workplaceId),
);

export default applyRoomAssignments;

/**
 * When each desk stops being bookable, for the desks that have a date at all.
 *
 * Keyed on the workplace id, which is also the desk's `featureId` — that equality is what ties
 * the drawing to the bookable row in the first place. Desks with no scheduled retirement are
 * simply absent, so a lookup miss means "not retiring" rather than "unknown".
 */
/**
 * The desks whose retirement has already been carried out.
 *
 * Kept apart from [retirementDatesOf] because the two answer different questions: that one says
 * *when* a desk goes, this one says whether it has *already gone*. Only the second decides whether
 * the editor may still offer to cancel it.
 */
export const appliedRetirementIdsOf = (
  assignments: readonly FloorPlanRoomAssignment[],
): Set<string> => new Set(
  assignments
    .filter((assignment) => !!assignment.retirementAppliedAt)
    .map((assignment) => assignment.workplaceId),
);

export const retirementDatesOf = (
  assignments: readonly FloorPlanRoomAssignment[],
): Map<string, string> => new Map(
  assignments
    .filter((assignment): assignment is FloorPlanRoomAssignment & { retiresOn: string } => !!assignment.retiresOn)
    .map((assignment) => [assignment.workplaceId, assignment.retiresOn]),
);
